import * as React from "react";
import { VariantProps, cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:disabled:opacity-75 dark:disabled:bg-muted dark:disabled:text-foreground/60 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
}

/**
 * Button component with multiple variants and sizes.
 * Based on shadcn/ui design patterns.
 *
 * @example
 * <Button>Click me</Button>
 * <Button variant="outline" size="sm">Small button</Button>
 * <Button variant="ghost" size="icon"><IconMenu /></Button>
 * <Button loading>Loading...</Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      fullWidth,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? React.Fragment : "button";
    const isDisabled = Boolean(disabled || loading);

    const buttonContent = (
      <>
        {loading && (
          <LoadingSpinner
            className={cn(
              "animate-spin",
              size === "sm" && "h-3 w-3",
              size === "default" && "h-4 w-4",
              size === "lg" && "h-5 w-5",
              size === "icon" && "h-4 w-4",
              children && "mr-2",
            )}
          />
        )}
        {children}
      </>
    );

    if (asChild) {
      return (
        <React.Fragment>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement<any>, {
                className: cn(buttonVariants({ variant, size, className })),
                disabled: isDisabled,
                children: buttonContent,
                ...props,
              });
            }
            return child;
          })}
        </React.Fragment>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }), fullWidth && "w-full")}
        ref={ref}
        disabled={isDisabled}
        type="button"
        {...props}
      >
        {buttonContent}
      </button>
    );
  },
);

Button.displayName = "Button";

/**
 * Loading spinner component used inside buttons.
 * Memoized to prevent unnecessary re-renders.
 */
const LoadingSpinner = React.memo(function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
});

export { Button, buttonVariants };
